/**
 * HVAC Node Component
 *
 * Custom React Flow node component for rendering HVAC equipment
 * with inlet/outlet ports and visual styling based on component type.
 * Shows sync status with IDF store.
 */

import { memo, useMemo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import type { HVACNodeData, FluidMedium, PortDirection } from './types';
import { getComponentDefinition } from './components';
import { useNodeSyncStatus } from './sync';

/**
 * Get handle position based on port position value
 */
function getHandleStyle(position: number, direction: PortDirection): React.CSSProperties {
  const side = direction === 'inlet' ? 'left' : 'right';
  return {
    top: `${String(position * 100)}%`,
    [side]: -8,
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: '2px solid white',
  };
}

/**
 * Get handle color based on fluid medium
 */
function getMediumColor(medium: FluidMedium): string {
  switch (medium) {
    case 'air':
      return '#64B5F6'; // Light blue for air
    case 'water':
      return '#1976D2'; // Dark blue for water
    case 'refrigerant':
      return '#AB47BC'; // Purple for refrigerant
    case 'steam':
      return '#BDBDBD'; // Gray for steam
    default:
      return '#90A4AE';
  }
}

/**
 * Get icon SVG for component type
 */
function ComponentIcon({ icon, color }: { icon: string; color: string }) {
  const iconColor = color;

  switch (icon) {
    case 'fan':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M12 11c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm2.39-6.77C14.27 4.08 14.12 4 13.96 4h-4c-.16 0-.31.08-.39.23l-1.88 3.17a.5.5 0 00.18.68l3.13 1.8c.15.09.34.09.49 0l3.13-1.8a.5.5 0 00.18-.68l-1.41-2.17zm-5.6 15.54c.08.15.23.23.39.23h4c.16 0 .31-.08.39-.23l1.88-3.17a.5.5 0 00-.18-.68l-3.13-1.8a.5.5 0 00-.49 0l-3.13 1.8a.5.5 0 00-.18.68l1.45 2.17zm9.98-9.16c-.08-.15-.23-.23-.39-.23h-3.17a.5.5 0 00-.43.25l-1.57 2.72a.5.5 0 000 .5l1.57 2.72a.5.5 0 00.43.25h3.17c.16 0 .31-.08.39-.23l1.18-2.05c.14-.25.14-.55 0-.8l-1.18-1.13zm-13.54 0c.08-.15.23-.23.39-.23h3.17a.5.5 0 01.43.25l1.57 2.72a.5.5 0 010 .5l-1.57 2.72a.5.5 0 01-.43.25H5.62c-.16 0-.31-.08-.39-.23l-1.18-2.05c-.14-.25-.14-.55 0-.8l1.18-1.13z" />
        </svg>
      );

    case 'snowflake':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M20.79 13.95l-1.44-.83 1.2-.69c.21-.12.27-.39.15-.6-.12-.21-.39-.27-.6-.15l-1.2.69v-1.38c0-.24-.2-.44-.44-.44s-.44.2-.44.44v2.26l-3.1 1.79-.01-3.57 1.96-1.13c.21-.12.27-.39.15-.6-.12-.21-.39-.27-.6-.15l-1.5.87V7.97l2.14-1.24 1.2.69c.21.12.48.05.6-.15.12-.21.05-.48-.15-.6l-1.2-.69 1.44-.83c.21-.12.27-.39.15-.6-.12-.21-.39-.27-.6-.15l-1.44.83V3.79c0-.24-.2-.44-.44-.44s-.44.2-.44.44v2.26l-2.14 1.24V4.31c0-.24-.2-.44-.44-.44s-.44.2-.44.44v2.98l-2.14-1.24V3.79c0-.24-.2-.44-.44-.44s-.44.2-.44.44v1.44l-1.44-.83c-.21-.12-.48-.05-.6.15-.12.21-.05.48.15.6l1.44.83-1.2.69c-.21.12-.27.39-.15.6.12.21.39.27.6.15l1.2-.69v1.38c0 .24.2.44.44.44s.44-.2.44-.44V5.66l3.1-1.79v3.57l-1.96 1.13c-.21.12-.27.39-.15.6.12.21.39.27.6.15l1.5-.87v2.38l-2.14 1.24-1.2-.69c-.21-.12-.48-.05-.6.15-.12.21-.05.48.15.6l1.2.69-1.44.83c-.21.12-.27.39-.15.6.12.21.39.27.6.15l1.44-.83v1.44c0 .24.2.44.44.44s.44-.2.44-.44v-2.26l2.14-1.24v2.98c0 .24.2.44.44.44s.44-.2.44-.44v-2.98l2.14 1.24v2.26c0 .24.2.44.44.44s.44-.2.44-.44v-1.44l1.44.83c.21.12.48.05.6-.15.12-.21.05-.48-.15-.6z" />
        </svg>
      );

    case 'flame':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M12 23c3.9 0 7-3.1 7-7 0-2.8-1.6-4.7-2.8-6.4C14.5 7.3 13 5.1 13 2c0-.6-.4-1-1-1-.5 0-.9.4-1 .9C10.6 5.5 8 8.3 6.4 11 5.5 12.4 5 14.1 5 16c0 3.9 3.1 7 7 7zm0-14c.6 1.2 1.5 2.4 2.3 3.5.8 1.1 1.7 2.3 2.3 3.9.2.5.3 1.1.3 1.6 0 2.8-2.2 5-5 5s-5-2.2-5-5c0-1.3.4-2.5 1-3.6.8-1.4 2.3-3.3 4.1-5.4z" />
        </svg>
      );

    case 'box':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M21 16.5c0 .38-.21.71-.53.88l-7.9 4.44c-.16.12-.36.18-.57.18-.21 0-.41-.06-.57-.18l-7.9-4.44A.991.991 0 013 16.5v-9c0-.38.21-.71.53-.88l7.9-4.44c.16-.12.36-.18.57-.18.21 0 .41.06.57.18l7.9 4.44c.32.17.53.5.53.88v9zM12 4.15L5 8v8l7 4 7-4V8l-7-3.85z" />
        </svg>
      );

    case 'split':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M14 4l2.29 2.29-2.88 2.88 1.42 1.42 2.88-2.88L20 10V4h-6zm-4 0H4v6l2.29-2.29 4.71 4.7V20h2v-8.41l-5.29-5.3L10 4z" />
        </svg>
      );

    case 'merge':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M17 20l-2.29-2.29 2.88-2.88-1.42-1.42-2.88 2.88L11 14h6v6zm-10 0h6l-2.29-2.29-4.71-4.7V4h-2v8.41l5.29 5.3L7 20z" />
        </svg>
      );

    case 'pump':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <circle cx="12" cy="12" r="8" fill="none" stroke={iconColor} strokeWidth="2" />
          <path d="M12 6v6l4 2" fill="none" stroke={iconColor} strokeWidth="2" />
        </svg>
      );

    case 'arrows-exchange':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z" />
        </svg>
      );

    case 'droplet':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8zm0 18c-3.35 0-6-2.57-6-6.2 0-2.34 1.95-5.44 6-9.14 4.05 3.7 6 6.79 6 9.14 0 3.63-2.65 6.2-6 6.2z" />
        </svg>
      );

    case 'wind':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M14.5 17c0 1.65-1.35 3-3 3s-3-1.35-3-3h2c0 .55.45 1 1 1s1-.45 1-1-.45-1-1-1H2v-2h9.5c1.65 0 3 1.35 3 3zM19 6.5C19 4.57 17.43 3 15.5 3S12 4.57 12 6.5h2c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S16.33 8 15.5 8H2v2h13.5c1.93 0 3.5-1.57 3.5-3.5zm-.5 4.5H2v2h16.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5v2c1.93 0 3.5-1.57 3.5-3.5S20.43 11 18.5 11z" />
        </svg>
      );

    case 'tower':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-7-2h2v-4h-2v4zm-4 0h2v-4H8v4zm8 0h2v-4h-2v4z" />
        </svg>
      );

    case 'grid':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M3 3v8h8V3H3zm6 6H5V5h4v4zm-6 4v8h8v-8H3zm6 6H5v-4h4v4zm4-16v8h8V3h-8zm6 6h-4V5h4v4zm-6 4v8h8v-8h-8zm6 6h-4v-4h4v4z" />
        </svg>
      );

    case 'square':
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <path d="M3 3h18v18H3V3zm16 16V5H5v14h14z" />
        </svg>
      );

    default:
      return (
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill={iconColor}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
        </svg>
      );
  }
}

/**
 * HVAC Node Component
 */
function HVACNodeComponent({ id, data, selected }: NodeProps<HVACNodeData>) {
  const definition = useMemo(
    () => getComponentDefinition(data.componentType),
    [data.componentType]
  );

  // Get sync status for this node
  const syncStatus = useNodeSyncStatus(id);

  if (!definition) {
    return (
      <div className="rounded-lg border-2 border-red-500 bg-red-100 p-3 dark:border-red-400 dark:bg-red-950" role="alert">
        <span className="text-red-700 dark:text-red-400">Unknown component: {data.componentType}</span>
      </div>
    );
  }

  const inletPorts = definition.ports.filter((p) => p.direction === 'inlet');
  const outletPorts = definition.ports.filter((p) => p.direction === 'outlet');

  return (
    <div
      className={`
        relative min-w-[160px] rounded-lg border-2 bg-card shadow-md transition-shadow
        ${selected ? 'border-blue-500 shadow-lg ring-2 ring-blue-300 dark:ring-blue-700' : 'border-border'}
        ${data.hasErrors ? 'border-red-500 dark:border-red-400' : ''}
      `}
      style={{
        borderTopColor: definition.color,
        borderTopWidth: 4,
      }}
      role="group"
      aria-label={`${data.name} - ${definition.displayName}`}
    >
      {/* Sync status indicator */}
      {syncStatus.isSynced && (
        <div
          className="absolute -right-1 -top-1 h-3 w-3 rounded-full border border-white bg-green-500"
          title={`Synced to IDF: ${syncStatus.idfObjectId || 'unknown'}`}
        />
      )}

      {/* Inlet Handles */}
      {inletPorts.map((port) => (
        <Handle
          key={port.id}
          id={port.id}
          type="target"
          position={Position.Left}
          style={{
            ...getHandleStyle(port.position, 'inlet'),
            backgroundColor: getMediumColor(port.medium),
          }}
          title={`${port.name} (${port.medium})`}
        />
      ))}

      {/* Outlet Handles */}
      {outletPorts.map((port) => (
        <Handle
          key={port.id}
          id={port.id}
          type="source"
          position={Position.Right}
          style={{
            ...getHandleStyle(port.position, 'outlet'),
            backgroundColor: getMediumColor(port.medium),
          }}
          title={`${port.name} (${port.medium})`}
        />
      ))}

      {/* Header */}
      <div
        className="flex items-center gap-2 border-b border-border px-3 py-2"
        style={{ backgroundColor: `${definition.color}15` }}
      >
        <ComponentIcon icon={definition.icon} color={definition.color} />
        <div className="flex-1 overflow-hidden">
          <div className="truncate text-sm font-medium text-foreground" title={data.name}>
            {data.name}
          </div>
          <div className="truncate text-xs text-muted-foreground">{definition.displayName}</div>
        </div>
      </div>

      {/* Ports summary */}
      <div className="px-3 py-2 text-xs text-muted-foreground">
        <div className="flex justify-between">
          <span>{inletPorts.length} inlet{inletPorts.length !== 1 ? 's' : ''}</span>
          <span>{outletPorts.length} outlet{outletPorts.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Error indicator */}
      {data.hasErrors && data.errors.length > 0 && (
        <div className="border-t border-red-200 bg-red-50 px-3 py-1 dark:border-red-800 dark:bg-red-950">
          <div className="text-xs text-red-600 dark:text-red-400" role="alert">{data.errors[0]}</div>
        </div>
      )}
    </div>
  );
}

export const HVACNode = memo(HVACNodeComponent);
